import { useFlowRuntime } from "../FlowRuntimeContext";
import React, { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";

import type {
  FlowComponentRenderProps,
  VideoPlayerComponentConfig,
} from "../types";
import HtmlText from "./HtmlText";

const playableStatuses = new Set(["available", "complete", "completed", "ready"]);

const VideoPlayerBlock: React.FC<
  FlowComponentRenderProps<VideoPlayerComponentConfig>
> = ({ config }) => {
  const { refreshMedia, refreshingMedia, mediaRevision } = useFlowRuntime();
  const [playbackFailed, setPlaybackFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const status = config.asset?.status?.trim().toLowerCase();
  const playbackUrl = config.asset?.playbackUrl;
  const isReady = Boolean(
    playbackUrl && status && playableStatuses.has(status),
  );
  const accessibleLabel = useMemo(
    () =>
      config.title?.trim() ||
      config.asset?.originalFilename?.trim() ||
      "Survey video",
    [config.asset?.originalFilename, config.title],
  );

  useEffect(() => {
    setPlaybackFailed(false);
    setRetryKey(0);
  }, [config.asset?.id, playbackUrl, status, mediaRevision]);

  return (
    <Stack spacing={1.25}>
      {config.title?.trim() ? (
        <HtmlText
          component="h2"
          html={config.title}
          sx={{
            color: "var(--cx-brand-text)",
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.35,
            m: 0,
          }}
        />
      ) : null}

      {isReady && !playbackFailed ? (
        <Box
          aria-label={accessibleLabel}
          component="video"
          controls
          key={`${playbackUrl}-${retryKey}`}
          onError={() => setPlaybackFailed(true)}
          playsInline
          poster={config.posterUrl}
          preload="metadata"
          src={playbackUrl}
          sx={{
            bgcolor: "#000",
            borderRadius: 1.5,
            display: "block",
            maxHeight: 480,
            objectFit: "contain",
            width: "100%",
          }}
        />
      ) : (
        <Stack
          aria-live="polite"
          role="status"
          spacing={1}
          sx={{
            alignItems: "center",
            bgcolor: "var(--cx-brand-button-bg)",
            border: "1px solid var(--cx-brand-border)",
            borderRadius: 1.5,
            color: "var(--cx-brand-text-muted)",
            justifyContent: "center",
            minHeight: 180,
            px: 2,
            py: 3,
            textAlign: "center",
          }}
        >
          <MovieRoundedIcon aria-hidden="true" sx={{ fontSize: 42 }} />
          <Typography sx={{ color: "inherit", fontWeight: 700 }} variant="body2">
            {playbackFailed
              ? "This video could not be loaded."
              : status === "loading" ? "This video is processing." : "This video is not available yet."}
          </Typography>
          {(playbackFailed || refreshMedia) ? (
            <Button
              disabled={refreshingMedia}
              onClick={() => {
                if (refreshMedia) { void refreshMedia(); return; }
                setPlaybackFailed(false);
                setRetryKey((current) => current + 1);
              }}
              size="small"
              variant="outlined"
            >
              {refreshMedia ? (refreshingMedia ? "Refreshing media…" : "Refresh media") : "Try again"}
            </Button>
          ) : null}
        </Stack>
      )}

      {config.caption?.trim() ? (
        <HtmlText
          component="div"
          html={config.caption}
          sx={{ color: "var(--cx-brand-text-muted)", fontSize: 14 }}
        />
      ) : null}
    </Stack>
  );
};

export default VideoPlayerBlock;
